import { Popover, PopoverButton, PopoverPanel, Transition } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import { useStore } from "@/stores/store";
import {
  ArrowPathIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  continueContentHubPolling,
  retryContentHubLive,
  useContentHubConnectionMetrics,
  useContentHubPollingMode,
  useContentHubStreamStatus,
} from "../hooks";
import type {
  ContentHubConnectionMetrics,
  ContentHubStreamStatus,
} from "../types";

const TROUBLESHOOTING_PATH = "/settings/troubleshooting";

/** Green when live, amber while (re)connecting, red once dropped to polling. */
type Tone = "live" | "warn" | "down";

function deriveTone(status: ContentHubStreamStatus, polling: boolean): Tone {
  if (polling) return "down";
  if (status === "connected") return "live";
  return "warn";
}

function toneLabel(status: ContentHubStreamStatus, tone: Tone): string {
  if (tone === "down") return "Polling";
  if (tone === "live") return "Live";
  return status === "connecting" ? "Connecting" : "Reconnecting";
}

const TONE_DOT: Record<Tone, string> = {
  live: "bg-green-500",
  warn: "bg-amber-500",
  down: "bg-red-500",
};

const TONE_TEXT: Record<Tone, string> = {
  live: "text-green-600 dark:text-green-400",
  warn: "text-amber-600 dark:text-amber-400",
  down: "text-red-600 dark:text-red-400",
};

/** "just now" / "12s ago" / "5m ago" / "2h ago". */
function timeAgo(ts: number | null, now: number): string {
  if (!ts) return "—";
  const s = Math.max(0, Math.round((now - ts) / 1000));
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

/** Compact elapsed duration since a timestamp: "42s" / "8m" / "2h 5m". */
function duration(ts: number | null, now: number): string {
  if (!ts) return "—";
  const s = Math.max(0, Math.round((now - ts) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

/** Seconds until the next scheduled reconnect, or 0 when none is pending. */
function secondsUntil(ts: number | null, now: number): number {
  if (!ts) return 0;
  return Math.max(0, Math.ceil((ts - now) / 1000));
}

function Dot({ tone, ping }: { tone: Tone; ping: boolean }) {
  return (
    <span className="relative flex h-2 w-2">
      {ping && (
        <span
          className={`absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping ${TONE_DOT[tone]}`}
        />
      )}
      <span
        className={`relative inline-flex h-2 w-2 rounded-full ${TONE_DOT[tone]}`}
      />
    </span>
  );
}

function MetricRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 py-0.5">
      <span className="text-gray-500 dark:text-gray-400">{label}</span>
      <span className="tabular-nums text-gray-800 dark:text-gray-200">
        {value}
      </span>
    </div>
  );
}

/** Panel body. Lives inside the open Popover panel only, so its 1s metrics
 *  interval runs solely while the panel is open. */
function StatusPanelBody({ onGoTroubleshooting }: { onGoTroubleshooting: () => void }) {
  const metrics: ContentHubConnectionMetrics = useContentHubConnectionMetrics();
  const now = Date.now();
  const tone = deriveTone(metrics.status, metrics.pollingMode);
  const retryIn = secondsUntil(metrics.nextRetryAt, now);
  const pollIn = secondsUntil(metrics.nextPollAt, now);

  // One-line reassurance tuned to the current state — the panel's main job is
  // to tell the user the feed is still trustworthy.
  const summary = metrics.preferPolling
    ? "Polling mode is on by choice. Live streaming is off and updates arrive on a fixed interval. Turn it off in Settings to go back to live."
    : tone === "live"
    ? "Live updates are streaming in real time."
    : tone === "down"
    ? "Live streaming is unavailable. Updates are arriving on a short delay via polling — nothing is missed."
    : "Reconnecting to the live stream. Missed updates are backfilled automatically on reconnect.";

  return (
    <div className="p-3 text-xs">
      <div className="mb-2 space-y-2">
        <p className="leading-snug text-gray-600 dark:text-gray-300">{summary}</p>
        {tone === "warn" && retryIn > 0 && (
          <p className="text-gray-500 dark:text-gray-400">
            Next attempt in {retryIn}s
          </p>
        )}
      </div>

      <div className="border-t border-gray-100 pt-2 dark:border-gray-800">
        {tone === "down" ? (
          <>
            {/* Polling mode: the SSE-frame times are meaningless, so report the
                refetch cadence instead — when we last pulled and when we pull
                next. */}
            <MetricRow
              label="Last fetched"
              value={timeAgo(metrics.lastPolledAt, now)}
            />
            <MetricRow
              label="Next poll in"
              value={pollIn > 0 ? `${pollIn}s` : "now"}
            />
            <MetricRow
              label="Refresh rate"
              value={`every ${Math.round(metrics.pollIntervalMs / 1000)}s`}
            />
          </>
        ) : (
          <>
            {metrics.status === "connected" && (
              <MetricRow
                label="Connected for"
                value={duration(metrics.connectedSince, now)}
              />
            )}
            <MetricRow
              label="Last update received"
              value={timeAgo(metrics.lastEventAt, now)}
            />
            <MetricRow
              label="Last signal"
              value={timeAgo(metrics.lastFrameAt, now)}
            />
          </>
        )}
        <MetricRow label="Active feeds" value={String(metrics.subscriberCount)} />
        {metrics.activeFilterCount > 0 && (
          <MetricRow
            label="Live filters"
            value={String(metrics.activeFilterCount)}
          />
        )}
      </div>

      {tone !== "live" && !metrics.preferPolling && (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            onClick={retryContentHubLive}
            className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2.5 py-1 font-medium text-white hover:bg-blue-700"
          >
            <ArrowPathIcon className="size-3.5" />
            Retry live
          </button>
          {tone === "warn" && (
            <button
              onClick={continueContentHubPolling}
              className="rounded-md border border-gray-200 px-2.5 py-1 font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              Continue with polling
            </button>
          )}
        </div>
      )}

      <button
        onClick={onGoTroubleshooting}
        className="mt-3 flex w-full items-center justify-center gap-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
      >
        <InformationCircleIcon className="size-3.5" />
        Connection troubleshooting
      </button>
    </div>
  );
}

/**
 * Global floating indicator for the shared content-hub stream. A small pill
 * (green "Live" pulse) that opens a panel with connection health and controls.
 * Replaces the per-widget stream badges. Mounted once in the main app window
 * (inside the router, for the troubleshooting link).
 */
export function ContentHubStatusFab() {
  const status = useContentHubStreamStatus();
  const polling = useContentHubPollingMode();
  // Sit at the bottom-left; when the desktop sidebar rail is open, shift right
  // of it so the two don't overlap.
  const desktopSidebarOpen = useStore((s: any) => s.desktopSidebarOpen);

  // Mounted once in the main window inside the router, so navigation is safe.
  const navigate = useNavigate();
  const goTroubleshooting = () => navigate(TROUBLESHOOTING_PATH);

  // Nothing is streaming (no content-hub feed mounted) — stay out of the way.
  if (status === "idle" && !polling) return null;

  const tone = deriveTone(status, polling);
  const label = toneLabel(status, tone);

  return (
    <Popover
      className={`fixed bottom-4 z-[60] no-drag print:hidden ${
        desktopSidebarOpen ? "left-4 lg:left-24" : "left-4"
      }`}
    >
      <PopoverButton
        title="Content-hub connection status"
        className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium shadow-lg outline-none transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800"
      >
        <Dot tone={tone} ping={tone !== "down"} />
        <span className={TONE_TEXT[tone]}>{label}</span>
      </PopoverButton>

      <Transition
        enter="transition ease-out duration-100"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <PopoverPanel className="absolute bottom-full left-0 mb-2 w-72 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900">
          {({ close }) => (
            <>
              <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2 dark:border-gray-800">
                <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200">
                  <Dot tone={tone} ping={tone !== "down"} />
                  {label}
                </span>
                <button
                  onClick={() => close()}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="size-4" />
                </button>
              </div>
              <StatusPanelBody
                onGoTroubleshooting={() => {
                  close();
                  goTroubleshooting();
                }}
              />
            </>
          )}
        </PopoverPanel>
      </Transition>
    </Popover>
  );
}

export default ContentHubStatusFab;
